/* eslint-disable no-console */
import Box from '@mui/material/Box';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PaymentData } from 'src/components/PaymentWidget/payment.type';
import { AdditionalInterestAndInsured } from 'src/configs/APISchemas/Application.schema';
import { PaymentPlanEnum } from 'src/configs/APISchemas/BindPolicy';
import { PaymentPlan } from 'src/configs/APISchemas/GetQuote';
import { ContactSupportId } from 'src/constants/constants';
import { ROUTES } from 'src/constants/routes';
import { useLocationSearch } from 'src/helpers/hooks/useLocationSearch';
import {
  bindPolicy,
  updateAdditionalParties,
} from 'src/pages/payment-msi/PaymentMsiV2/api';
import { PaymentFormBody } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentFormBody';
import { PaymentFormHeader } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentFormHeader';
import { PaymentSidebar } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentSidebar';
import {
  useFormWatcher,
  useLoading,
} from 'src/pages/payment-msi/PaymentMsiV2/hooks';
import { getBindPolicyMutationSerializer } from 'src/pages/payment-msi/PaymentMsiV2/serializers';
import {
  BillingAddress,
  InterestedParty,
} from 'src/pages/payment-msi/PaymentMsiV2/types';
import {
  updatePurchasePolicyAdditionalData,
  updatePurchasePolicyInput,
} from 'src/redux/actions/purchasePolicy';
import { PurchasePolicyInput } from 'src/redux/reducer/v2/purchasePolicy';
import {
  getPurchasePolicyAdditionalData,
  getPurchasePolicyInput,
} from 'src/redux/selectors/purchasePolicy';
import { getCompanyVendor } from 'src/redux/selectors/shared';
import { AppTheme } from 'src/themes/muiTheme';
import {
  checkIfSubmitDisabled,
  getPaymentFormDefaultValues,
  getPaymentPlanOptions,
  mapBuildingAddressToBillingAddress,
  purgeAdditionalInsuredsForForm,
  purgeAdditionalInsuredsForStore,
  purgeInterestedPartyForStore,
  scrollToFirstError,
  validatePaymentForm,
} from './utils';

export interface PaymentFormValues {
  paymentPlan: PaymentPlanEnum;
  interestedParty: InterestedParty;
  additionalInsured: AdditionalInterestAndInsured[];
  paymentData: PaymentData | null;
  billingAddress: BillingAddress;
  agreed: boolean;
}

const paymentMsiV2Styles = {
  pageContainer: (theme: AppTheme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    const mobile = theme.breakpoints.down(mobileMaxSize);
    const tabletMaxSize = theme.breakpoints.values.lg + 1;
    const tablet = theme.breakpoints.down(tabletMaxSize);

    return {
      marginLeft: '3rem',
      marginRight: '3rem',
      marginTop: '80px',
      marginBottom: '60px',
      [tablet]: {
        marginTop: 0,
        marginLeft: 0,
        marginRight: 0,
        marginBottom: '60px',
      },
      [mobile]: {
        marginBottom: '30px',
      },
    };
  },
  formContainer: (theme: AppTheme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    const mobile = theme.breakpoints.down(mobileMaxSize);
    const tabletMaxSize = theme.breakpoints.values.lg + 1;
    const tablet = theme.breakpoints.down(tabletMaxSize);

    return {
      display: 'grid',
      gridTemplateColumns: '1.3fr 0.7fr',
      columnGap: '32px',
      [tablet]: {
        display: 'flex',
        flexDirection: 'column-reverse',
        rowGap: '42px',
      },
      [mobile]: {
        display: 'flex',
        flexDirection: 'column-reverse',
        columnGap: 0,
        rowGap: '30px',
      },
    };
  },
  formSection: (theme: AppTheme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    const mobile = theme.breakpoints.down(mobileMaxSize);
    const tabletMaxSize = theme.breakpoints.values.lg + 1;
    const tablet = theme.breakpoints.down(tabletMaxSize);

    return {
      [tablet]: {
        paddingLeft: '96px',
        paddingRight: '96px',
      },
      [mobile]: {
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    };
  },
};

interface PaymentMsiV2Props {
  paymentPlans: PaymentPlan[];
  landlordData: InterestedParty | null;
}

export const PaymentMsiV2 = ({
  paymentPlans,
  landlordData,
}: PaymentMsiV2Props) => {
  const navigate = useNavigate();
  const getUrlWithParams = useLocationSearch();
  const confirmationPageUrl = getUrlWithParams(ROUTES.CONFIRMATION_PATH);
  const [isLoading, startLoading, stopLoading] = useLoading();

  const dispatch = useDispatch();
  const purchasePolicyInput = useSelector(getPurchasePolicyInput);
  const purchasePolicyAdditionalData = useSelector(
    getPurchasePolicyAdditionalData,
  );
  const companyVendor = useSelector(getCompanyVendor);

  const paymentPlanOptions = getPaymentPlanOptions(paymentPlans);
  const buildingAddress = mapBuildingAddressToBillingAddress({
    buildingAddress: purchasePolicyInput.buildingAddress.value,
    isCompany: Boolean(companyVendor),
    buildingUnit: purchasePolicyInput.buildingUnit,
  });
  const defaultFormValues = getPaymentFormDefaultValues({
    interestedParty: landlordData,
    additionalInterests: purchasePolicyInput.additionalInterests,
    additionalInsured: purchasePolicyInput.additionalInsuredRoommates,
    agreed: null,
    buildingAddress: buildingAddress,
    paymentPlan: null,
  });
  const form = useForm<PaymentFormValues>({
    defaultValues: defaultFormValues,
  });
  useFormWatcher(form.watch, (data) => {
    const interestedParty = purgeInterestedPartyForStore(data.interestedParty);
    const additionalInsureds = purgeAdditionalInsuredsForStore(
      data.additionalInsured ?? [],
    );

    const updatePayload: Partial<PurchasePolicyInput> = {
      additionalInsuredRoommates: additionalInsureds,
    };

    if (interestedParty) {
      const interestedPartyFistName =
        interestedParty.fullName.split(' ')[0] ?? '';
      const interestedPartyLastName =
        interestedParty.fullName.split(' ')[1] ?? '.';
      updatePayload.additionalInterests = [
        {
          firstName: interestedPartyFistName,
          lastName: interestedPartyLastName,
          email: interestedParty.email,
        },
      ];
    }

    dispatch(updatePurchasePolicyInput(updatePayload));
  });

  const isSubmitDisabled = checkIfSubmitDisabled(form.watch());

  async function onSubmit(formData: PaymentFormValues) {
    const { additionalInsured, interestedParty } = formData;
    const purgedAdditionalInsured =
      purgeAdditionalInsuredsForForm(additionalInsured);
    const purgedInterestedParty = purgeInterestedPartyForStore(interestedParty);

    const { errors, isValid } = validatePaymentForm({
      ...formData,
      additionalInsured: purgedAdditionalInsured,
      interestedParty: purgedInterestedParty,
    });

    if (!isValid) {
      for (const error of errors) {
        form.setError(error.fieldName as keyof PaymentFormValues, {
          message: error.message,
        });
      }
      scrollToFirstError(errors);

      return;
    }

    startLoading();
    form.clearErrors();
    const genericUpdateAdditionalPartiesErrorMessage = `We are having some trouble, Please contact support for help. id: ${ContactSupportId.quoteInformation}`;
    const genericBindPolicyErrorMessage = `We are having some trouble, Please contact support for help. id: ${ContactSupportId.paymentMSICompletePurchase}`;

    try {
      const updateAdditionalPartiesResponse = await updateAdditionalParties({
        additionalInsured: purgedAdditionalInsured,
        applicationId: purchasePolicyAdditionalData.id,
        buildingAddress: purchasePolicyInput.address,
        buildingId: purchasePolicyInput.buildingId,
        buildingName: purchasePolicyInput.buildingName,
        communityId: purchasePolicyAdditionalData.communityId,
        interestedParty: purgedInterestedParty,
        landlordData,
      });

      if (!updateAdditionalPartiesResponse) {
        toast(genericUpdateAdditionalPartiesErrorMessage, { type: 'error' });
        stopLoading();
        return;
      }

      const bindPolicyMutationData = getBindPolicyMutationSerializer({
        billingAddress: formData.billingAddress,
        paymentPlan: formData.paymentPlan,
        paymentData: formData.paymentData as PaymentData,
        purchasePolicyAdditionalData,
      });
      const bindPolicyResponse = await bindPolicy(bindPolicyMutationData);

      if (!bindPolicyResponse) {
        toast(genericBindPolicyErrorMessage, { type: 'error' });
        stopLoading();
        return;
      }
      if (bindPolicyResponse.errors) {
        const errorMessage =
          bindPolicyResponse.errors[0]?.message ??
          genericBindPolicyErrorMessage;
        toast(errorMessage, {
          type: 'error',
          bodyStyle: {
            whiteSpace: 'pre-line',
          },
        });
        stopLoading();
        return;
      }

      dispatch(
        updatePurchasePolicyAdditionalData({
          ...bindPolicyResponse,
          subscription: formData.paymentPlan,
        }),
      );

      navigate(confirmationPageUrl);
    } catch (error) {
      toast(genericBindPolicyErrorMessage, { type: 'error' });
    } finally {
      stopLoading();
    }
  }

  return (
    <Box sx={paymentMsiV2Styles.pageContainer}>
      <FormProvider {...form}>
        <Box
          noValidate={true}
          component={'form'}
          onSubmit={form.handleSubmit(onSubmit)}
          sx={paymentMsiV2Styles.formContainer}
        >
          <Box component={'section'} sx={paymentMsiV2Styles.formSection}>
            <PaymentFormHeader />
            <PaymentFormBody
              isLoading={isLoading}
              buildingAddress={buildingAddress}
              landlordData={landlordData}
              isSubmitDisabled={isSubmitDisabled}
              paymentPlanOptions={paymentPlanOptions}
            />
          </Box>
          <PaymentSidebar paymentPlans={paymentPlanOptions} />
        </Box>
      </FormProvider>
    </Box>
  );
};
